import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users as UsersIcon, FolderKanban, UserPlus, Trash2, Mail } from "lucide-react";
import { api } from "@/data/api";
import { npmApiErrorMessage } from "@/data/npmState";
import { useAsync } from "@/hooks/useAsync";
import { healthBadge } from "@/lib/health";
import { AsyncBoundary, EmptyState, Panel, StatusBadge, ProjectAvatar } from "@/components/common";
import { Button } from "@/components/ui/button";

const ROLE_LABEL = { client_admin: "Client Admin", client_viewer: "Viewer", client: "Cliente (legado)" };
const input = "rounded border border-line bg-background px-2 py-1.5 text-sm";

export default function ClientDetail() {
  const { id } = useParams();
  const q = useAsync(() => api.getClientOverview(id), [id]);
  const [form, setForm] = useState({ email: "", name: "", role: "client_viewer" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const data = q.data;

  const invite = async (event) => {
    event.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const r = await api.inviteClientMember(id, { ...form, email: form.email || null });
      setResult({ ok: true, text: r.created ? `Convite criado para ${r.email}.` : `Acesso adicionado para ${r.email}.`, link: r.link });
      setForm({ email: "", name: "", role: "client_viewer" });
      q.reload();
    } catch (e) {
      setResult({ ok: false, text: npmApiErrorMessage(e) });
    } finally {
      setBusy(false);
    }
  };
  const toggleInternal = async () => {
    setResult(null);
    try {
      await api.setClientInternal(id, !data.client.internal);
      q.reload();
    } catch (e) {
      setResult({ ok: false, text: npmApiErrorMessage(e) });
    }
  };
  const remove = async (member) => {
    try {
      await api.removeClientMember(id, member.user_id);
      q.reload();
    } catch (e) {
      setResult({ ok: false, text: npmApiErrorMessage(e) });
    }
  };

  return <div className="space-y-6 animate-fade-up">
    <Link to="/clientes" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Voltar para Clientes</Link>
    <AsyncBoundary loading={q.loading} error={q.error}>
      {data && <>
        <div className="rounded-xl border border-line bg-surface p-5">
          <div className="flex items-center gap-3">
            <ProjectAvatar name={data.client.name} size="lg" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground" data-testid="client-name">{data.client.name}</h1>
              <p className="text-sm text-muted-foreground"><Mail className="mr-1 inline h-3.5 w-3.5" />{data.client.contact_email} <span className="text-xs">(contato — não concede acesso)</span></p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {data.client.internal && <span className="rounded border border-line px-2 py-0.5 text-xs text-muted-foreground">Interno · sem Portal</span>}
              <Button size="sm" variant="outline" onClick={toggleInternal} data-testid="client-internal-toggle">{data.client.internal ? "Remover marcação interna" : "Marcar como owner interno"}</Button>
            </div>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <Panel title="Projetos" icon={FolderKanban} bodyClassName="space-y-2">
            {data.projects.length ? data.projects.map((p) => <Link key={p.id} to={`/projetos/${p.id}`} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm hover:bg-white/[0.02]" data-testid="client-project">
              <span><span className="font-medium text-foreground">{p.name}</span> <span className="text-xs text-muted-foreground">{p.domains.join(", ") || "sem site"} · {p.services} serviço(s)</span></span>
              <StatusBadge {...healthBadge(p.health)} size="sm" pulse={false} />
            </Link>) : <EmptyState icon={FolderKanban} title="Nenhum Projeto" />}
          </Panel>

          <Panel title="Membros do Portal" icon={UsersIcon} bodyClassName="space-y-3">
            {!data.portal_enabled ? <p className="text-sm text-muted-foreground">Clients internos não possuem acesso ao Portal.</p> : <>
              {data.members.length ? <ul className="space-y-1" data-testid="client-members">{data.members.map((m) => <li key={m.user_id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm">
                <span><span className="font-medium text-foreground">{m.name}</span> <span className="text-xs text-muted-foreground">{m.email} · {m.roles.map((r) => ROLE_LABEL[r] || r).join(", ")}</span></span>
                <span className="flex items-center gap-2"><StatusBadge status={m.status === "invited" ? "pending" : m.status} label={m.status === "invited" ? "Convidado" : undefined} size="sm" pulse={false} />
                  <Button size="sm" variant="ghost" onClick={() => remove(m)} aria-label={`Remover ${m.email}`}><Trash2 className="h-3.5 w-3.5" /></Button></span>
              </li>)}</ul> : <p className="text-sm text-muted-foreground">Nenhum membro. O acesso ao Portal depende de membership explícita.</p>}
              <form onSubmit={invite} className="grid gap-2 sm:grid-cols-4" data-testid="client-invite-form">
                <input className={`${input} sm:col-span-2`} type="email" name="email" placeholder={`${data.client.contact_email} (padrão)`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input className={input} name="name" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <select className={input} name="role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="client_viewer">Viewer</option><option value="client_admin">Client Admin</option>
                </select>
                <Button type="submit" size="sm" disabled={busy} className="sm:col-span-4"><UserPlus className="h-3.5 w-3.5" /> Convidar para o Portal</Button>
              </form>
            </>}
            {result && <div role="alert" className={`rounded-lg border px-3 py-2 text-sm ${result.ok ? "border-ok/30 bg-ok/10 text-ok" : "border-err/30 bg-err/10 text-err"}`}>
              {result.text}{result.link && <span className="mt-1 block break-all font-mono text-xs text-foreground">Link (válido 24h): {result.link}</span>}
            </div>}
          </Panel>
        </div>
      </>}
    </AsyncBoundary>
  </div>;
}
